import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListSubheader,
  Menu, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import MoreVertIcon       from '@mui/icons-material/MoreVert'
import PersonAddIcon      from '@mui/icons-material/PersonAdd'
import CheckCircleIcon    from '@mui/icons-material/CheckCircleOutlined'
import CancelIcon         from '@mui/icons-material/CancelOutlined'
import DeleteIcon         from '@mui/icons-material/DeleteOutlined'
import SendIcon           from '@mui/icons-material/Send'
import SwapHorizIcon      from '@mui/icons-material/SwapHoriz'
import SchoolIcon         from '@mui/icons-material/School'
import LogoutIcon         from '@mui/icons-material/Logout'
import EditIcon           from '@mui/icons-material/EditOutlined'
import VpnKeyIcon         from '@mui/icons-material/VpnKeyOutlined'
import VisibilityIcon     from '@mui/icons-material/Visibility'
import { supabase }       from '../../../supabaseClient'
import { readFunctionError } from '../../../lib/functionError'
import { useAuth }        from '../../../auth/AuthContext'
import { LifecycleState } from '../../../types'
import EnrollDialog          from '../../../components/students/EnrollDialog'
import AssignMentorDialog    from '../../../components/students/AssignMentorDialog'
import EnrollInClassDialog   from '../../../components/students/EnrollInClassDialog'
import ChangeClassDialog     from '../../../components/students/ChangeClassDialog'
import EditStudentDialog     from '../../../components/students/EditStudentDialog'
import { ParentSignupChip, loadParentSignup, ParentSignup } from '../../../components/students/parentSignup'
import SetStudentPasswordDialog from '../../../components/students/SetStudentPasswordDialog'
import TopicSubmissionsQueue from '../../../components/students/TopicSubmissionsQueue'

interface StudentRow {
  user_id:           string
  student_id:        string
  email:             string
  first_name:        string
  last_name:         string
  grade:             number | null
  school:            string | null
  lifecycle_state:   LifecycleState
  created_at:        string
  primary_mentor_id:   string | null
  primary_mentor_name: string | null
  /**
   * Source of the primary mentor:
   *   'lead'  — explicit student_mentor row (mentor_role='lead', active)
   *   'class' — implicit via class_mentor of the student's class
   *   null    — neither; truly unassigned
   */
  primary_mentor_source: 'lead' | 'class' | null
  current_class_id:   string | null
  current_class_name: string | null
  /** Parent portal signup status (best across linked parents). */
  parent_signup:      ParentSignup
  /** Active project's current step (mentor-view column). */
  current_step:       number | null
}

interface StaffOption {
  user_id:    string
  first_name: string
  last_name:  string
}

const FILTER_LABELS = {
  active:    'Active',
  completed: 'Completed',
  withdrawn: 'Withdrawn',
} as const

type Filter = keyof typeof FILTER_LABELS

const FILTER_TO_STATE: Record<Filter, LifecycleState> = {
  active:    'enrolled_active',
  completed: 'enrolled_completed',
  withdrawn: 'withdrawn',
}

const STAFF_ROLES = ['admin', 'office_manager', 'instructor', 'mentor']

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function nameOrEmail(r: StudentRow) {
  const name = `${r.first_name} ${r.last_name}`.trim()
  return name || r.email
}

export default function ManagerStudentsPage() {
  const { session, role } = useAuth()
  const isManagerOrAdmin = role === 'admin' || role === 'office_manager'
  const isInstructorOrMentor = role === 'instructor' || role === 'mentor'
  // Current step is a mentor-view detail (mentors/instructors + admin), not for
  // office managers. Parent signup is the admin/office-manager column.
  const showStep = role === 'admin' || role === 'instructor' || role === 'mentor'

  const [rows,           setRows]           = useState<StudentRow[]>([])
  const [staff,          setStaff]          = useState<StaffOption[]>([])
  const [filter,         setFilter]         = useState<Filter>('active')
  const [mentorFilter,   setMentorFilter]   = useState<string>('all')   // 'all' | 'unassigned' | <user_id>
  const [search,         setSearch]         = useState('')
  const [loading,        setLoading]        = useState(true)
  const [error,          setError]          = useState<string | null>(null)
  const [menuAnchor,     setMenuAnchor]     = useState<null | HTMLElement>(null)
  const [menuRow,        setMenuRow]        = useState<StudentRow | null>(null)
  const [confirmComplete, setConfirmComplete] = useState<StudentRow | null>(null)
  const [confirmWithdraw, setConfirmWithdraw] = useState<StudentRow | null>(null)
  const [confirmDelete,   setConfirmDelete]   = useState<StudentRow | null>(null)
  const [actioning,       setActioning]       = useState<string | null>(null)
  const [enrollOpen,      setEnrollOpen]      = useState(false)
  const [assignOpen,      setAssignOpen]      = useState<StudentRow | null>(null)
  const [classEnrollOpen, setClassEnrollOpen] = useState<StudentRow | null>(null)
  const [classMoveOpen,   setClassMoveOpen]   = useState<StudentRow | null>(null)
  const [confirmUnenroll, setConfirmUnenroll] = useState<StudentRow | null>(null)
  const [editOpen,        setEditOpen]        = useState<StudentRow | null>(null)
  const [pwOpen,          setPwOpen]          = useState<StudentRow | null>(null)

  // Stable target for EditStudentDialog: only changes when the selected row
  // changes, so a background list reload (onSaved) doesn't recreate the object
  // and retrigger the dialog's load effect.
  const editTarget = useMemo(
    () => editOpen ? {
      user_id: editOpen.user_id, email: editOpen.email,
      first_name: editOpen.first_name, last_name: editOpen.last_name,
    } : null,
    [editOpen],
  )

  async function load() {
    if (!session) return
    setLoading(true)

    // ── Step 1: pull all student_profiles in enrolled lifecycle states ───────
    const enrolledStates: LifecycleState[] = ['enrolled_active', 'enrolled_completed', 'withdrawn']
    const { data: profiles } = await supabase
      .from('student_profile')
      .select('student_id, user_id, grade, school_name, lifecycle_state, created_at')
      .in('lifecycle_state', enrolledStates)

    if (!profiles?.length) { setRows([]); setLoading(false); return }

    // ── Step 2: pull users for those student_ids ─────────────────────────────
    const userIds = profiles.map(p => p.user_id)
    const { data: users } = await supabase
      .from('users')
      .select('user_id, email, first_name, last_name')
      .in('user_id', userIds)
    const userMap: Record<string, any> = Object.fromEntries((users ?? []).map(u => [u.user_id, u]))

    // ── Step 3: pull active lead mentor assignments ──────────────────────────
    const studentIds = profiles.map(p => p.student_id)
    const { data: mentorAssignments } = await supabase
      .from('student_mentor')
      .select('student_id, mentor_id')
      .in('student_id', studentIds)
      .eq('mentor_role', 'lead')
      .eq('status', 'active')
    const mentorByStudent: Record<string, string> = Object.fromEntries(
      (mentorAssignments ?? []).map(m => [m.student_id, m.mentor_id])
    )

    // ── Step 3b: pull active class enrollments + class names ─────────────────
    const { data: enrollmentRows } = await supabase
      .from('class_enrollment')
      .select('student_id, class_id')
      .in('student_id', studentIds)
      .eq('status', 'active')
    const classByStudent: Record<string, string> = Object.fromEntries(
      (enrollmentRows ?? []).map(e => [e.student_id, e.class_id])
    )
    const classIds = Array.from(new Set(Object.values(classByStudent)))
    let classNameMap: Record<string, string> = {}
    // First class_mentor per class (by created_at) acts as the implicit primary
    // mentor when there's no explicit student_mentor lead row.
    let classPrimaryMentor: Record<string, string> = {}
    if (classIds.length > 0) {
      const [{ data: classRows }, { data: classMentorRows }] = await Promise.all([
        supabase.from('class').select('class_id, name').in('class_id', classIds),
        supabase.from('class_mentor').select('class_id, user_id, created_at')
          .in('class_id', classIds)
          .order('created_at', { ascending: true }),
      ])
      classNameMap = Object.fromEntries((classRows ?? []).map(c => [c.class_id, c.name]))
      for (const cm of classMentorRows ?? []) {
        if (!classPrimaryMentor[cm.class_id]) classPrimaryMentor[cm.class_id] = cm.user_id
      }
    }

    // ── Step 4: pull names for any mentor_ids we found, plus all staff ───────
    const { data: staffRows } = await supabase
      .from('users')
      .select('user_id, first_name, last_name')
      .in('role', STAFF_ROLES)
      .eq('status', 'active')
      .order('last_name')
    const staffMap: Record<string, StaffOption> = Object.fromEntries(
      (staffRows ?? []).map(s => [s.user_id, s])
    )
    setStaff(staffRows ?? [])

    // ── Step 5: scope filter for instructor / mentor ─────────────────────────
    // Instructors and mentors see the union of:
    //   - their primary mentees (student_mentor.mentor_id = me, role='lead', active)
    //   - students in classes where they're listed in class_mentor
    let scopedStudentIds: Set<string> | null = null
    if (isInstructorOrMentor) {
      scopedStudentIds = new Set<string>()
      // (a) primary mentees
      for (const m of mentorAssignments ?? []) {
        if (m.mentor_id === session.user.id) scopedStudentIds.add(m.student_id)
      }
      // (b) class mentor assignments
      const { data: classMentorRows } = await supabase
        .from('class_mentor')
        .select('class_id')
        .eq('user_id', session.user.id)
      const myClassIds = (classMentorRows ?? []).map(c => c.class_id)
      if (myClassIds.length > 0) {
        const { data: enrollments } = await supabase
          .from('class_enrollment')
          .select('student_id')
          .in('class_id', myClassIds)
          .eq('status', 'active')
        for (const e of enrollments ?? []) scopedStudentIds.add(e.student_id)
      }
    }

    // ── Step 5b: parent signup status + active-project current step ──────────
    const [parentSignupByStudent, { data: projectRows }] = await Promise.all([
      loadParentSignup(studentIds),
      supabase.from('project')
        .select('student_id, current_step')
        .in('student_id', studentIds)
        .eq('status', 'active'),
    ])
    const stepByStudent: Record<string, number> = Object.fromEntries(
      (projectRows ?? []).map(p => [p.student_id, p.current_step])
    )

    // ── Step 6: build final rows ─────────────────────────────────────────────
    const merged: StudentRow[] = profiles
      .filter(p => !scopedStudentIds || scopedStudentIds.has(p.student_id))
      .map(p => {
        const classId   = classByStudent[p.student_id] ?? null
        const className = classId ? (classNameMap[classId] ?? null) : null

        // Resolve primary mentor with fallback to class teacher.
        const explicitLeadId = mentorByStudent[p.student_id] ?? null
        const classMentorId  = classId ? (classPrimaryMentor[classId] ?? null) : null
        const resolvedId     = explicitLeadId ?? classMentorId
        const resolvedSource: 'lead' | 'class' | null = explicitLeadId
          ? 'lead'
          : (classMentorId ? 'class' : null)
        const mentor = resolvedId ? staffMap[resolvedId] : null
        const mentorName = mentor
          ? `${mentor.first_name} ${mentor.last_name}`.trim() || resolvedId
          : null

        return {
          user_id:             p.user_id,
          student_id:          p.student_id,
          email:               userMap[p.user_id]?.email      ?? '',
          first_name:          userMap[p.user_id]?.first_name ?? '',
          last_name:           userMap[p.user_id]?.last_name  ?? '',
          grade:               p.grade,
          school:              p.school_name,
          lifecycle_state:     p.lifecycle_state,
          created_at:          p.created_at,
          primary_mentor_id:    resolvedId,
          primary_mentor_name:  mentorName,
          primary_mentor_source: resolvedSource,
          current_class_id:    classId,
          current_class_name:  className,
          parent_signup:       parentSignupByStudent[p.student_id] ?? 'none',
          current_step:        stepByStudent[p.student_id] ?? null,
        }
      })
    setRows(merged.sort((a, b) => a.last_name.localeCompare(b.last_name)))
    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  const visible = useMemo(() => rows.filter(r => {
    if (r.lifecycle_state !== FILTER_TO_STATE[filter]) return false
    if (mentorFilter === 'unassigned' && r.primary_mentor_id) return false
    if (mentorFilter !== 'all' && mentorFilter !== 'unassigned' && r.primary_mentor_id !== mentorFilter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!r.email.toLowerCase().includes(q) &&
          !r.first_name.toLowerCase().includes(q) &&
          !r.last_name.toLowerCase().includes(q) &&
          !(r.school ?? '').toLowerCase().includes(q)) return false
    }
    return true
  }), [rows, filter, mentorFilter, search])

  // Group the visible students by class for a class-organized layout. Named
  // classes come first (alphabetical); students with no class fall into a
  // trailing "No class" group. Within each group, rows keep their last-name sort.
  const groupedByClass = useMemo(() => {
    const map = new Map<string, { classId: string | null; className: string; students: StudentRow[] }>()
    for (const r of visible) {
      const key = r.current_class_id ?? '__none__'
      if (!map.has(key)) {
        map.set(key, { classId: r.current_class_id, className: r.current_class_name ?? 'No class', students: [] })
      }
      map.get(key)!.students.push(r)
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.classId === null) return 1
      if (b.classId === null) return -1
      return a.className.localeCompare(b.className)
    })
  }, [visible])

  async function doComplete(row: StudentRow) {
    setActioning(row.student_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('mark-completed', {
      body: { student_id: row.student_id },
    })
    setActioning(null); setConfirmComplete(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  async function doWithdraw(row: StudentRow) {
    setActioning(row.student_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('withdraw-student', {
      body: { student_id: row.student_id },
    })
    setActioning(null); setConfirmWithdraw(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  async function doDelete(row: StudentRow) {
    setActioning(row.user_id)
    const { error: invokeErr } = await supabase.functions.invoke('delete-user', {
      body: { user_id: row.user_id },
    })
    setActioning(null); setConfirmDelete(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else setRows(prev => prev.filter(r => r.user_id !== row.user_id))
  }

  async function doUnenroll(row: StudentRow) {
    setActioning(row.student_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('unenroll-from-class', {
      body: { student_id: row.student_id },
    })
    setActioning(null); setConfirmUnenroll(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  async function resendInvite(row: StudentRow) {
    setActioning(row.user_id); setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('send-student-login-link', {
      body: { student_email: row.email },
    })
    setActioning(null)
    if (invokeErr || data?.error || data?.ok === false) {
      setError(data?.error ?? 'Could not send the login link. Please try again.')
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>Students</Typography>
          <Typography variant="body2" color="text.secondary">
            {isInstructorOrMentor ? 'Students you mentor or teach.' : 'Enrolled students with full app access.'}
          </Typography>
        </Box>
        {isManagerOrAdmin && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setEnrollOpen(true)}
          >
            Enroll student
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TopicSubmissionsQueue />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          exclusive size="small" value={filter}
          onChange={(_, v) => v && setFilter(v)}
        >
          {(Object.keys(FILTER_LABELS) as Filter[]).map(f => (
            <ToggleButton key={f} value={f} sx={{ textTransform: 'none' }}>
              {FILTER_LABELS[f]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {isManagerOrAdmin && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Primary Mentor</InputLabel>
            <Select
              label="Primary Mentor"
              value={mentorFilter}
              onChange={e => setMentorFilter(e.target.value)}
            >
              <MenuItem value="all">All mentors</MenuItem>
              <MenuItem value="unassigned">— Unassigned —</MenuItem>
              {staff.map(s => {
                const fullName = `${s.first_name} ${s.last_name}`.trim() || s.user_id
                return <MenuItem key={s.user_id} value={s.user_id}>{fullName}</MenuItem>
              })}
            </Select>
          </FormControl>
        )}

        <TextField
          size="small" placeholder="Search by name, email, school…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240, ml: 'auto' }}
        />
      </Box>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
          {filter === 'active'    ? 'No active students.'
         : filter === 'completed' ? 'No completed students.'
         : 'No withdrawn students.'}
        </Typography>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Primary Mentor</TableCell>
                {showStep && <TableCell sx={{ fontWeight: 600 }}>Current step</TableCell>}
                {isManagerOrAdmin && <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedByClass.map(g => (
                <React.Fragment key={g.classId ?? '__none__'}>
                  <TableRow>
                    <TableCell
                      colSpan={8 + (showStep ? 1 : 0) + (isManagerOrAdmin ? 1 : 0)}
                      sx={{ bgcolor: 'grey.100', fontWeight: 700, py: 1, borderBottom: '2px solid', borderColor: 'divider' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color={g.classId ? 'primary' : 'disabled'} />
                        {g.className}
                        <Typography component="span" variant="caption" color="text.secondary">
                          · {g.students.length} student{g.students.length === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {g.students.map(r => (
                    <TableRow key={r.student_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{nameOrEmail(r)}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.grade ?? '—'}</TableCell>
                      <TableCell>{r.school ?? '—'}</TableCell>
                      <TableCell>
                        {r.primary_mentor_name
                          ? <Typography variant="body2">{r.primary_mentor_name}</Typography>
                          : <Chip size="small" color="warning" label="Needs mentor" variant="outlined" />}
                      </TableCell>
                      {showStep && (
                        <TableCell>
                          {r.current_step
                            ? <Typography variant="body2">Step {r.current_step}</Typography>
                            : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </TableCell>
                      )}
                      {isManagerOrAdmin && (
                        <TableCell>
                          <ParentSignupChip value={r.parent_signup} />
                        </TableCell>
                      )}
                      <TableCell>
                        {r.lifecycle_state === 'enrolled_active'    && <Chip size="small" color="success" label="Active" />}
                        {r.lifecycle_state === 'enrolled_completed' && <Chip size="small" color="default" label="Completed" />}
                        {r.lifecycle_state === 'withdrawn'          && <Chip size="small" color="error"   label="Withdrawn" />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{fmtDate(r.created_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        {isManagerOrAdmin ? (
                          <IconButton size="small" onClick={e => { setMenuAnchor(e.currentTarget); setMenuRow(r) }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        ) : isInstructorOrMentor ? (
                          <IconButton
                            size="small"
                            title="View as student"
                            onClick={() => window.open(`/view-as/${r.student_id}/dashboard`, '_blank')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuRow(null) }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem
          onClick={() => {
            if (menuRow) window.open(`/view-as/${menuRow.student_id}/dashboard`, '_blank')
            setMenuAnchor(null)
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} /> View as student
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />

        {/* ACCOUNT */}
        <ListSubheader sx={{ fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.8, color: 'text.secondary' }}>
          ACCOUNT
        </ListSubheader>
        <MenuItem onClick={() => { setEditOpen(menuRow); setMenuAnchor(null) }}>
          <EditIcon fontSize="small" sx={{ mr: 1.5 }} /> Edit details
        </MenuItem>
        <MenuItem onClick={() => { setPwOpen(menuRow); setMenuAnchor(null) }}>
          <VpnKeyIcon fontSize="small" sx={{ mr: 1.5 }} /> Set password
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />

        {/* MENTOR */}
        <ListSubheader sx={{ fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.8, color: 'text.secondary' }}>
          MENTOR
        </ListSubheader>
        {menuRow && (
          <MenuItem onClick={() => { setAssignOpen(menuRow); setMenuAnchor(null) }}>
            <SwapHorizIcon fontSize="small" sx={{ mr: 1.5 }} />
            {menuRow.primary_mentor_source === 'lead' ? 'Change Mentor' : 'Assign Mentor'}
          </MenuItem>
        )}
        {menuRow?.primary_mentor_source === 'lead' && (
          <MenuItem onClick={() => { setAssignOpen(menuRow); setMenuAnchor(null) }}>
            <CancelIcon fontSize="small" sx={{ mr: 1.5 }} /> Unassign Mentor…
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />

        {/* CLASS */}
        <ListSubheader sx={{ fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.8, color: 'text.secondary' }}>
          CLASS
        </ListSubheader>
        {menuRow && !menuRow.current_class_id && (
          <MenuItem onClick={() => { setClassEnrollOpen(menuRow); setMenuAnchor(null) }}>
            <SchoolIcon fontSize="small" sx={{ mr: 1.5 }} /> Enroll in class
          </MenuItem>
        )}
        {menuRow?.current_class_id && (
          <MenuItem onClick={() => { setClassMoveOpen(menuRow); setMenuAnchor(null) }}>
            <SwapHorizIcon fontSize="small" sx={{ mr: 1.5 }} /> Change class
          </MenuItem>
        )}
        {menuRow?.current_class_id && (
          <MenuItem onClick={() => { setConfirmUnenroll(menuRow); setMenuAnchor(null) }}>
            <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> Unenroll
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />

        {/* LIFECYCLE */}
        <ListSubheader sx={{ fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.8, color: 'text.secondary' }}>
          LIFECYCLE
        </ListSubheader>
        {menuRow?.lifecycle_state === 'enrolled_active' && (
          <MenuItem onClick={() => { setConfirmComplete(menuRow); setMenuAnchor(null) }}>
            <CheckCircleIcon fontSize="small" sx={{ mr: 1.5 }} /> Mark Completed
          </MenuItem>
        )}
        {menuRow && menuRow.lifecycle_state !== 'withdrawn' && (
          <MenuItem onClick={() => { setConfirmWithdraw(menuRow); setMenuAnchor(null) }}>
            <CancelIcon fontSize="small" sx={{ mr: 1.5 }} /> Withdraw
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />

        {/* OTHER */}
        <MenuItem onClick={() => { menuRow && resendInvite(menuRow); setMenuAnchor(null) }}>
          <SendIcon fontSize="small" sx={{ mr: 1.5 }} /> Send login link
        </MenuItem>
        <MenuItem onClick={() => { setConfirmDelete(menuRow); setMenuAnchor(null) }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete account
        </MenuItem>
      </Menu>

      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} onEnrolled={load} />

      <AssignMentorDialog
        open={!!assignOpen}
        studentId={assignOpen?.student_id ?? null}
        studentLabel={assignOpen ? `${nameOrEmail(assignOpen)} (${assignOpen.email})` : ''}
        currentMentorId={assignOpen?.primary_mentor_source === 'lead' ? assignOpen.primary_mentor_id : null}
        currentMentorName={assignOpen?.primary_mentor_source === 'lead' ? assignOpen.primary_mentor_name : null}
        onClose={() => setAssignOpen(null)}
        onAssigned={load}
      />

      <EnrollInClassDialog
        open={!!classEnrollOpen}
        studentId={classEnrollOpen?.student_id ?? null}
        studentLabel={classEnrollOpen ? `${nameOrEmail(classEnrollOpen)} (${classEnrollOpen.email})` : ''}
        onClose={() => setClassEnrollOpen(null)}
        onEnrolled={load}
      />

      <ChangeClassDialog
        open={!!classMoveOpen}
        studentId={classMoveOpen?.student_id ?? null}
        studentLabel={classMoveOpen ? `${nameOrEmail(classMoveOpen)} (${classMoveOpen.email})` : ''}
        currentClassId={classMoveOpen?.current_class_id ?? null}
        currentClassName={classMoveOpen?.current_class_name ?? null}
        onClose={() => setClassMoveOpen(null)}
        onMoved={load}
      />

      <EditStudentDialog
        open={!!editOpen}
        target={editTarget}
        onClose={() => setEditOpen(null)}
        onSaved={load}
      />

      <SetStudentPasswordDialog
        open={!!pwOpen}
        target={pwOpen ? {
          user_id: pwOpen.user_id, email: pwOpen.email,
          first_name: pwOpen.first_name, last_name: pwOpen.last_name,
        } : null}
        onClose={() => setPwOpen(null)}
        onSaved={() => {}}
      />

      <Dialog open={!!confirmUnenroll} onClose={() => setConfirmUnenroll(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Unenroll from class?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmUnenroll ? nameOrEmail(confirmUnenroll) : ''}</strong> will be removed from
            class <strong>{confirmUnenroll?.current_class_name ?? ''}</strong>. Their work is preserved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmUnenroll(null)}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            onClick={() => confirmUnenroll && doUnenroll(confirmUnenroll)}
            disabled={actioning === confirmUnenroll?.student_id}
          >
            {actioning === confirmUnenroll?.student_id ? <CircularProgress size={18} /> : 'Unenroll'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmComplete} onClose={() => setConfirmComplete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Mark research as completed?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmComplete ? nameOrEmail(confirmComplete) : ''}</strong> will be moved to
            the Completed list. They retain read-only access to their project.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmComplete(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => confirmComplete && doComplete(confirmComplete)}
            disabled={actioning === confirmComplete?.student_id}
          >
            {actioning === confirmComplete?.student_id ? <CircularProgress size={18} /> : 'Mark Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmWithdraw} onClose={() => setConfirmWithdraw(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Withdraw student?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmWithdraw ? nameOrEmail(confirmWithdraw) : ''}</strong> will lose access
            to the app. The account is preserved and can be re-enrolled later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmWithdraw(null)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={() => confirmWithdraw && doWithdraw(confirmWithdraw)}
            disabled={actioning === confirmWithdraw?.student_id}
          >
            {actioning === confirmWithdraw?.student_id ? <CircularProgress size={18} /> : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete account?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{confirmDelete ? nameOrEmail(confirmDelete) : ''}</strong> and
            all associated data. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={() => confirmDelete && doDelete(confirmDelete)}
            disabled={actioning === confirmDelete?.user_id}
          >
            {actioning === confirmDelete?.user_id ? <CircularProgress size={18} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
